import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { landListingsAPI } from '../../services/landListingsAPI';
import { getMockLandDetails } from '../../services/mockLandData';

// Async thunk for fetching land details by ID
export const fetchLandDetails = createAsyncThunk(
  'landDetails/fetchLandDetails',
  async (landId, { rejectWithValue }) => {
    try {
      const raw = await landListingsAPI.getListingById(landId);
      // Accept multiple possible response shapes:
      // 1. { success: true, data: {...} }
      // 2. { data: {...}, success?: undefined }
      // 3. Direct object of land details (has id / title / etc.)
      let normalized;
      if (raw && raw.success === true && raw.data) {
        normalized = raw.data;
      } else if (raw && raw.data && typeof raw.data === 'object' && (raw.data.id || raw.data.title)) {
        normalized = raw.data; // some endpoints wrap but omit success flag
      } else if (raw && raw.id) {
        normalized = raw; // direct listing object
      } else if (raw && raw.success === false) {
        return rejectWithValue(raw.error || 'Failed to fetch land details');
      }

      if (!normalized) {
        // Force fallback to mock data by throwing so catch executes
        throw new Error('Unrecognized land details response shape');
      }

      return normalized;
    } catch (error) {
      console.log('[landDetails] Primary API path failed, attempting mock data...', error.message);

      // Try mock data
      try {
        const mockResult = await getMockLandDetails(landId);
        console.log('[landDetails] Using mock data for land:', landId);
        return mockResult.data;
      } catch (mockError) {
        if (error.response) {
          const errorMessage = error.response.data?.error || error.response.data?.message || 'Failed to fetch land details';
          return rejectWithValue(errorMessage);
        }
        return rejectWithValue(error.message || 'Network error occurred');
      }
    }
  }
);

const landDetailsSlice = createSlice({
  name: 'landDetails',
  initialState: {
    currentLand: null,
    loading: false,
    error: null,
    lastFetchedId: null,
  },
  reducers: {
    clearLandDetails: (state) => {
      state.currentLand = null;
      state.error = null;
      state.lastFetchedId = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLandDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLandDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLand = action.payload;
        state.lastFetchedId = action.payload.id;
        state.error = null;
      })
      .addCase(fetchLandDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentLand = null;
      });
  },
});

export const { clearLandDetails, clearError } = landDetailsSlice.actions;

// Selectors
export const selectCurrentLand = (state) => state.landDetails.currentLand;
export const selectLandDetailsLoading = (state) => state.landDetails.loading;
export const selectLandDetailsError = (state) => state.landDetails.error;
export const selectLastFetchedId = (state) => state.landDetails.lastFetchedId;

export default landDetailsSlice.reducer;
